import React from 'react';

import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { Empty, Pagination, Skeleton } from 'antd';
import classNames from 'classnames';

import IntlMessages from '@src/util/IntlMessages';

import type {
  ActiveUserListItem,
  ActiveUserListPagination,
  ExecutionComplianceType,
  SortKey,
  SortState,
} from '../../types';
import ActiveUserListRow from './ActiveUserListRow';
import styles from './ActiveUserList.module.less';

interface ActiveUserListProps {
  users: ActiveUserListItem[];
  loading: boolean;
  pagination: ActiveUserListPagination;
  isMobile: boolean;
  nowMs: number;
  timeZone: string;
  sortState: SortState;
  onSortChange: (state: SortState) => void;
  onFindUser: (user: ActiveUserListItem) => void;
  onTimeline: (userId: string) => void;
  onSchedules: (user: ActiveUserListItem) => void;
  onOpenCompliance: (user: ActiveUserListItem, type: ExecutionComplianceType) => void;
}

interface SortableHeaderCellProps {
  labelId: string;
  sortKey: SortKey;
  sortState: SortState;
  onSortChange: (state: SortState) => void;
}

const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({
  labelId,
  sortKey,
  sortState,
  onSortChange,
}) => {
  const isActive = sortState.key === sortKey;
  const nextOrder = isActive && sortState.order === 'asc' ? 'desc' : 'asc';

  return (
    <button
      type="button"
      className={classNames(styles.headerCell, isActive && styles.headerActive)}
      onClick={() => onSortChange({ key: sortKey, order: nextOrder })}
    >
      <span className={styles.headerLabel}>
        <IntlMessages id={labelId} />
      </span>
      <span className={styles.sortCarets}>
        <CaretUpOutlined
          className={classNames(isActive && sortState.order === 'asc' && styles.caretActive)}
        />
        <CaretDownOutlined
          className={classNames(isActive && sortState.order === 'desc' && styles.caretActive)}
        />
      </span>
    </button>
  );
};

const ActiveUserList: React.FC<ActiveUserListProps> = ({
  users,
  loading,
  pagination,
  isMobile,
  nowMs,
  timeZone,
  sortState,
  onSortChange,
  onFindUser,
  onTimeline,
  onSchedules,
  onOpenCompliance,
}) => {
  const pageUsers = users.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize,
  );

  if (loading) {
    return (
      <div className={styles.loading}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className={styles.empty}>
        <Empty description={<IntlMessages id="general.empty.state" />} />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {!isMobile && (
        <div className={styles.header}>
          <SortableHeaderCell
            labelId="activeUserDashboard.user.column"
            sortKey="displayName"
            sortState={sortState}
            onSortChange={onSortChange}
          />
          <SortableHeaderCell
            labelId="activeUserDashboard.eventsToday.column"
            sortKey="eventsToday"
            sortState={sortState}
            onSortChange={onSortChange}
          />
          <SortableHeaderCell
            labelId="activeUserDashboard.schedule.list.column"
            sortKey="scheduleCompliance"
            sortState={sortState}
            onSortChange={onSortChange}
          />
          <SortableHeaderCell
            labelId="activeUserDashboard.task.list.column"
            sortKey="taskCompliance"
            sortState={sortState}
            onSortChange={onSortChange}
          />
          <SortableHeaderCell
            labelId="activeUserDashboard.checkpointRoute.list.column"
            sortKey="checkpointRouteCompliance"
            sortState={sortState}
            onSortChange={onSortChange}
          />
          <SortableHeaderCell
            labelId="activeUserDashboard.battery.column"
            sortKey="battery"
            sortState={sortState}
            onSortChange={onSortChange}
          />
          <div className={styles.headerCellStatic} />
        </div>
      )}
      <div className={styles.list}>
        {pageUsers.map(user => (
          <ActiveUserListRow
            key={user.id}
            user={user}
            isMobile={isMobile}
            nowMs={nowMs}
            timeZone={timeZone}
            onFindUser={onFindUser}
            onTimeline={onTimeline}
            onSchedules={onSchedules}
            onOpenCompliance={onOpenCompliance}
          />
        ))}
      </div>
      {pagination.total > pagination.pageSize && (
        <div className={styles.pagination}>
          <Pagination
            size="small"
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={pagination.onChange}
            showSizeChanger={false}
            showLessItems
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(ActiveUserList);
